import { stateFromMastery } from './catalog';
import type { PilotMisconceptionSignal, PilotQuestionInstance, PilotSkillProgress } from './types';

function parseBinaryPrompt(prompt: string): { a: number; b: number; op: '+' | '−' } | null {
  const match = prompt.match(/^\s*(\d+)\s*([+−])\s*(\d+)\s*$/);
  if (!match) return null;
  return { a: Number(match[1]), op: match[2] as '+' | '−', b: Number(match[3]) };
}

export function detectPilotMisconceptions(question: PilotQuestionInstance, firstResponse: number): PilotMisconceptionSignal[] {
  if (firstResponse === question.answer) return [];
  const signals: PilotMisconceptionSignal[] = [];
  const binary = parseBinaryPrompt(question.prompt);

  if (question.skillCode === 'BK-MTH-OPS-ADD-025' && binary?.op === '+' && firstResponse === question.answer - 10) {
    signals.push({
      code: 'NO_REGROUPING',
      confidence: 'medium',
      explanation: 'Jawaban cocok dengan pola menjumlahkan digit tanpa membawa 1 puluhan hasil regrouping.',
    });
  }

  if (question.skillCode === 'BK-MTH-OPS-SUB-025' && binary?.op === '−') {
    const wrongByDigit = (Math.abs(Math.floor(binary.a / 10) - Math.floor(binary.b / 10)) * 10) + Math.abs((binary.a % 10) - (binary.b % 10));
    if (firstResponse === wrongByDigit) {
      signals.push({
        code: 'SMALLER_MINUS_LARGER_DIGIT',
        confidence: 'high',
        explanation: 'Jawaban cocok dengan pola mengurangi digit yang lebih kecil dari digit yang lebih besar pada tiap nilai tempat.',
      });
    }
  }

  if (question.skillCode === 'BK-MTH-NUM-PV-003') {
    const n = Number(question.prompt.match(/^(\d+)/)?.[1]);
    if (Number.isFinite(n) && firstResponse === n % 10) {
      signals.push({
        code: 'PLACE_VALUE_REVERSAL',
        confidence: 'medium',
        explanation: 'Jawaban tampak menggunakan digit satuan ketika soal meminta jumlah puluhan.',
      });
    }
  }

  if ((question.skillCode === 'BK-MTH-OPS-ADD-023' || question.skillCode === 'BK-MTH-OPS-SUB-023') && firstResponse !== question.answer) {
    signals.push({
      code: 'TEN_VALUE_CONFUSION',
      confidence: 'low',
      explanation: 'Kesalahan terjadi pada soal konseptual pertukaran 10 satuan dan 1 puluhan; perlu evidence tambahan.',
    });
  }

  return signals;
}

export function evaluatePilotEvidence(input: {
  question: PilotQuestionInstance;
  firstAttemptCorrect: boolean;
  finalCorrect: boolean;
  correctionCount: number;
  hintCount: number;
  misconceptionSignals: PilotMisconceptionSignal[];
}) {
  let weight = input.firstAttemptCorrect ? 1 : input.finalCorrect ? 0.45 : 0;
  if (input.hintCount >= 2) weight *= 0.8;
  if (input.question.reasonCode === 'spaced_review' || input.question.reasonCode === 'mastery_check') weight *= 1.1;
  weight = Math.max(0, Math.min(1, Number(weight.toFixed(2))));

  let masteryDelta = 0;
  if (!input.finalCorrect) masteryDelta = -4;
  else if (input.firstAttemptCorrect) masteryDelta = input.question.reasonCode === 'mastery_check' || input.question.reasonCode === 'spaced_review' ? 4 : 3;
  else if (input.misconceptionSignals.length > 0) masteryDelta = 0;
  else masteryDelta = 1;

  return { weight, masteryDelta };
}

export function updatePilotSkillProgress(input: {
  current: PilotSkillProgress;
  question: PilotQuestionInstance;
  firstAttemptCorrect: boolean;
  finalCorrect: boolean;
  correctionCount: number;
  hintCount: number;
  completedAt: string;
  misconceptionSignals: PilotMisconceptionSignal[];
}) {
  const evidence = evaluatePilotEvidence(input);
  const evidenceCount = input.current.evidenceCount + 1;
  const reviewPasses = input.current.reviewPasses + ((input.firstAttemptCorrect && (input.question.reasonCode === 'spaced_review' || input.question.reasonCode === 'mastery_check')) ? 1 : 0);
  const misconceptionSignals = input.current.misconceptionSignals + input.misconceptionSignals.length;
  const masteryPercent = Math.max(0, Math.min(100, input.current.masteryPercent + evidence.masteryDelta));
  let state = stateFromMastery(masteryPercent, { evidenceCount, reviewPasses });
  if (misconceptionSignals >= 2 && masteryPercent < 70) state = 'remedial';

  const reviewDue = new Date(input.completedAt);
  reviewDue.setDate(reviewDue.getDate() + (state === 'mastered' ? 7 : 3));

  const next: PilotSkillProgress = {
    ...input.current,
    masteryPercent,
    state,
    evidenceCount,
    firstAttemptCorrect: input.current.firstAttemptCorrect + (input.firstAttemptCorrect ? 1 : 0),
    finalCorrect: input.current.finalCorrect + (input.finalCorrect ? 1 : 0),
    reviewPasses,
    misconceptionSignals,
    lastActivityAt: input.completedAt,
    ...(input.question.reasonCode === 'spaced_review' || input.question.reasonCode === 'mastery_check' ? { lastReviewAt: input.completedAt } : {}),
    reviewDueAt: reviewDue.toISOString(),
  };

  return { next, evidenceWeight: evidence.weight };
}
